import { i18n } from '@quadralize/i18n'
import { cn } from '@quadralize/utils'
import { X } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { LabelWrapper } from '../label/label'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  label?: string
  labelRight?: React.ReactNode
  helperText?: React.ReactNode
  enableAutofill?: boolean
  isLoading?: boolean
  clearable?: boolean
  clearLabel?: string
  selectOnFocus?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      error,
      startIcon,
      endIcon,
      label,
      labelRight,
      helperText,
      id,
      required,
      enableAutofill = false,
      autoComplete,
      isLoading = false,
      clearable = false,
      clearLabel,
      value,
      onChange,
      disabled,
      readOnly,
      selectOnFocus,
      onFocus,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation('ui', { i18n })
    const generatedId = React.useId()
    const inputId = id || generatedId
    const inputRef = React.useRef<HTMLInputElement>(null)
    const resolvedClearLabel = clearLabel ?? t('input.clearLabel')
    const [hasInputValue, setHasInputValue] = React.useState(() => {
      if (value !== undefined && value !== null) {
        return String(value).length > 0
      }

      if (props.defaultValue !== undefined && props.defaultValue !== null) {
        return String(props.defaultValue).length > 0
      }

      return false
    })

    // Chrome specific hack to disable autofill by default
    const resolvedAutoComplete = enableAutofill
      ? autoComplete
      : autoComplete || (type === 'password' ? 'new-password' : 'one-time-code')

    const hasEndIcon = !!endIcon || isLoading
    const hasTrailingControl = hasEndIcon || clearable
    const showClearButton = clearable && !hasEndIcon && hasInputValue && !disabled && !readOnly

    React.useEffect(() => {
      if (value === undefined || value === null) {
        return
      }

      setHasInputValue(String(value).length > 0)
    }, [value])

    const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()

      const input = inputRef.current
      if (!input) {
        return
      }

      const inputValueSetter =
        Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set ?? null

      inputValueSetter?.call(input, '')
      input.dispatchEvent(new Event('input', { bubbles: true }))
      input.focus()
      setHasInputValue(false)
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setHasInputValue(event.target.value.length > 0)
      onChange?.(event)
    }

    const setRefs = (node: HTMLInputElement | null) => {
      inputRef.current = node

      if (typeof ref === 'function') {
        ref(node)
        return
      }

      if (ref) {
        ref.current = node
      }
    }

    return (
      <LabelWrapper
        label={label}
        id={inputId}
        required={required}
        error={error}
        labelRight={labelRight}
        helperText={helperText}
      >
        <div className="relative w-full">
          {startIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary">
              {startIcon}
            </div>
          )}
          <input
            type={type}
            id={inputId}
            autoComplete={resolvedAutoComplete}
            className={cn(
              'flex h-11 w-full rounded-md border border-input-border bg-input-bg px-3 py-2 text-sm text-text-primary transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-text-tertiary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus-ring disabled:cursor-not-allowed disabled:opacity-50 hover:border-input-border-hover [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none',
              startIcon && 'pl-9',
              hasTrailingControl && 'pr-9',
              error && 'border-error focus-visible:ring-error',
              className,
            )}
            aria-describedby={props['aria-describedby']}
            value={value}
            onChange={handleChange}
            onFocus={(e) => {
              if (selectOnFocus) {
                e.currentTarget.select()
              }
              onFocus?.(e)
            }}
            disabled={disabled}
            readOnly={readOnly}
            ref={setRefs}
            {...props}
          />
          {(hasEndIcon || showClearButton) && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary">
              {showClearButton ? (
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={handleClear}
                  onPointerDown={(event) => event.stopPropagation()}
                  className="grid h-5 w-5 place-items-center text-text-tertiary hover:text-text-primary focus:outline-none transition-colors cursor-pointer"
                  aria-label={resolvedClearLabel}
                >
                  <X className="h-4 w-4 opacity-70 hover:opacity-100" strokeWidth={3} />
                </button>
              ) : isLoading && !endIcon ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                endIcon
              )}
            </div>
          )}
        </div>
      </LabelWrapper>
    )
  },
)
Input.displayName = 'Input'

export { Input }
